import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Link,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { indicatorsAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { IndicatorsSkeleton } from '../components/indicators';

function Indicators() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [indicators, setIndicators] = useState([]);
  const [filteredIndicators, setFilteredIndicators] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [editForm, setEditForm] = useState({
    indicator_name: '',
    pillar_code: '',
    good_direction: 1,
    weight: '',
    description: '',
    data_source: '',
  });

  useEffect(() => {
    fetchIndicators();
    fetchPillars();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = indicators.filter(
        (ind) =>
          ind.indicator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ind.pillar_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ind.indicator_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredIndicators(filtered);
    } else {
      setFilteredIndicators(indicators);
    }
  }, [searchTerm, indicators]);

  const fetchIndicators = async () => {
    try {
      setLoading(true);
      const response = await indicatorsAPI.getIndicators();
      setIndicators(response.data);
      setFilteredIndicators(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching indicators:', error);
      setLoading(false);
    }
  };

  const fetchPillars = async () => {
    try {
      const response = await indicatorsAPI.getPillarWeights();
      setPillars(response.data);
    } catch (error) {
      console.error('Error fetching pillars:', error);
    }
  };

  const handleEditClick = (indicator) => {
    setSelectedIndicator(indicator);
    setEditForm({
      indicator_name: indicator.indicator_name || '',
      pillar_code: indicator.pillar_code || '',
      good_direction: indicator.good_direction || 1,
      weight: indicator.weight ? (indicator.weight * 100).toFixed(2) : '',
      description: indicator.description || '',
      data_source: indicator.data_source || '',
    });
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    try {
      const updateData = {
        indicator_name: editForm.indicator_name,
        pillar_code: editForm.pillar_code,
        good_direction: parseInt(editForm.good_direction),
        weight: editForm.weight ? parseFloat(editForm.weight) / 100 : null,
        description: editForm.description,
        data_source: editForm.data_source,
      };
      
      await indicatorsAPI.updateIndicator(selectedIndicator.indicator_id, updateData);
      setOpenEditDialog(false);
      fetchIndicators();
    } catch (error) {
      console.error('Error updating indicator:', error);
      alert('Error updating indicator: ' + error.message);
    }
  };

  const handleDeleteClick = (indicator) => {
    setSelectedIndicator(indicator);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await indicatorsAPI.deleteIndicator(selectedIndicator.indicator_id);
      setOpenDeleteDialog(false);
      fetchIndicators();
    } catch (error) {
      console.error('Error deleting indicator:', error);
      alert('Error deleting indicator: ' + error.message);
    }
  };

  const pillarColor = (code) => {
    const m = { GROWTH: '#059669', VALUATION: '#0891b2', MOMENTUM: '#d97706', QUALITY: '#3b82f6' };
    return m[code] || '#6b7280';
  };

  if (loading) {
    return <IndicatorsSkeleton />;
  }

  return (
    <Box sx={{ px: 0, py: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {t('indicators.title', 'Indicators Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t('indicators.subtitle', 'Configure and manage economic indicators for analysis')}
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('indicators.searchPlaceholder', 'Search by ID, name, or pillar...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
        />
      </Box>

      {/* Indicators Table */}
      <Box sx={{
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        borderRadius: 2.5, overflow: 'hidden',
      }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
            {t('indicators.title', 'Indicators')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredIndicators.length} {t('indicators.indicatorsCount', 'indicators')}
            {searchTerm && ` / ${indicators.length}`}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { label: t('indicators.indicatorId', 'ID'), minW: 90 },
                  { label: t('indicators.indicatorName', 'Name'), minW: 160 },
                  { label: t('indicators.pillar', 'Pillar'), minW: 80 },
                  { label: t('indicators.goodDirection', 'Dir'), minW: 50, align: 'center' },
                  { label: t('indicators.weight', 'Weight'), minW: 70 },
                  { label: t('indicators.source', 'Source'), minW: 70 },
                  { label: '', minW: 60, align: 'center' },
                ].map((col, i) => (
                  <TableCell
                    key={i}
                    align={col.align || 'left'}
                    sx={{
                      fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1.25,
                      minWidth: col.minW, whiteSpace: 'nowrap', bgcolor: 'background.paper',
                      borderBottom: '2px solid', borderColor: 'divider',
                      textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredIndicators.map((indicator) => (
                <TableRow
                  key={indicator.indicator_id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', fontFeatureSettings: '"tnum"' }}>
                      {indicator.indicator_id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                      {indicator.indicator_name}
                    </Typography>
                    {indicator.description && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.3 }}>
                        {indicator.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    <Chip
                      label={indicator.pillar_code}
                      size="small"
                      onClick={() => navigate('/pillar')}
                      sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                        bgcolor: `${pillarColor(indicator.pillar_code)}14`,
                        color: pillarColor(indicator.pillar_code),
                        border: 'none', borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Tooltip title={indicator.good_direction === 1 ? 'Higher is better' : 'Lower is better'} arrow>
                      {indicator.good_direction === 1 ? (
                        <TrendingUpIcon sx={{ color: '#059669', fontSize: 18 }} />
                      ) : (
                        <TrendingDownIcon sx={{ color: '#dc2626', fontSize: 18 }} />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, fontFeatureSettings: '"tnum"', color: indicator.weight ? 'text.primary' : 'text.disabled' }}>
                      {indicator.weight ? `${(indicator.weight * 100).toFixed(2)}%` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    {indicator.data_source ? (
                      <Link href={indicator.data_source} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                        {t('indicators.viewSource', 'Source')}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.78rem' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.25, px: 0.5 }}>
                    <Tooltip title="Edit" arrow>
                      <IconButton size="small" onClick={() => handleEditClick(indicator)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton size="small" onClick={() => handleDeleteClick(indicator)} sx={{ color: 'text.disabled', ml: 0.5, '&:hover': { color: 'error.main' } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          {t('indicators.editIndicator', 'Edit Indicator')}: {selectedIndicator?.indicator_id}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('indicators.indicatorName', 'Indicator Name')}
              fullWidth
              value={editForm.indicator_name}
              onChange={(e) => setEditForm({ ...editForm, indicator_name: e.target.value })}
            />
            <TextField
              label={t('indicators.pillarCode', 'Pillar Code')}
              select
              fullWidth
              value={editForm.pillar_code}
              onChange={(e) => setEditForm({ ...editForm, pillar_code: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">{t('indicators.selectPillar', 'Select Pillar')}</option>
              {pillars.map((pillar) => (
                <option key={pillar.pillar_code} value={pillar.pillar_code}>
                  {pillar.pillar_code} - {pillar.pillar_name}
                </option>
              ))}
            </TextField>
            <TextField
              label={t('indicators.goodDirection', 'Good Direction')}
              select
              fullWidth
              value={editForm.good_direction}
              onChange={(e) => setEditForm({ ...editForm, good_direction: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value={1}>{t('indicators.up', 'Up (Higher is better)')}</option>
              <option value={-1}>{t('indicators.down', 'Down (Lower is better)')}</option>
            </TextField>
            <TextField
              label={t('indicators.weightPercent', 'Weight (%)')}
              type="number"
              fullWidth
              value={editForm.weight}
              onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
              slotProps={{ htmlInput: { step: "0.01" } }}
            />
            <TextField
              label={t('indicators.description', 'Description')}
              fullWidth
              multiline
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
            <TextField
              label={t('indicators.dataSourceUrl', 'Data Source URL')}
              fullWidth
              value={editForm.data_source}
              onChange={(e) => setEditForm({ ...editForm, data_source: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ fontWeight: 600 }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained" 
            sx={{ fontWeight: 600, px: 3 }}
          >
            {t('indicators.saveChanges', 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {t('indicators.deleteIndicator', 'Delete Indicator')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('indicators.deleteConfirmation', 
              `Are you sure you want to delete indicator "${selectedIndicator?.indicator_name}" (${selectedIndicator?.indicator_id})? This action cannot be undone.`,
              { 
                name: selectedIndicator?.indicator_name, 
                id: selectedIndicator?.indicator_id 
              }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ fontWeight: 600 }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{ fontWeight: 600, px: 3 }}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Indicators;
